import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Modal, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faSync, faTrash, faUserTie, faUsers, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import { useOrderDetailViewOnly } from './OrderDetailViewOnlyContext';
import {
	useReMatchOrderPerformanceRulesMutation,
	useUpdateOrderPerformanceRuleMutation,
	useDeleteOrderPerformanceRuleMutation,
} from '../../../../../slices/ordersApiSlice';
import { RULE_TYPES, getRuleTypeBadgeVariant } from '../../../../performance-rule/constants';
import AppScrollArea from '../../../../../components/common/AppScrollArea';
import SubtleBadge from '../../../../../components/common/SubtleBadge';
import SystemAlert from '../../../../../components/modal/SystemAlert';
import ProfitMarginRangesEditor from '../../../../performance-rule/components/ProfitMarginRangesEditor';
import formatCurrency from '../../../../../utils/formatCurrency';
import { getOrderPerformanceBasisView } from '../../../../../utils/order/orderPerformanceBasisView';
import { markLocalOrderMutation } from '../../../../../utils/order/orderLocalMutationGuard';
import { useOrderPerformanceFeatures } from '../../../../../providers/OrderPerformanceFeaturesProvider';
import PerformanceRulesRematchOverlay, {
	isPerformanceRulesBusy,
	waitForNextPaint,
} from './PerformanceRulesRematchOverlay';
import { useOrderPerformanceRulesRematchHold } from '../../../../../providers/OrderPerformanceRulesRematchHoldProvider';

const ROLE_LABELS = {
	assistant: '销售助理',
	sale: '销售员',
	salesSupervisor: '销售主管',
};

const ROLE_ICONS = {
	assistant: faUserTie,
	sale: faUsers,
	salesSupervisor: faUserShield,
};

const MATCH_REASON_LABELS = {
	pair_and_user: '精确匹配',
	order_level: '整单固定',
	default: '默认规则',
	no_match: '未匹配',
	no_supervisor: '无主管',
};

const ROLE_ORDER = ['assistant', 'sale', 'salesSupervisor'];
const RULE_TYPE_LABELS = {
	fixed: '固定金额',
	percentage: '百分比',
	profitMarginRanges: '利润率分段',
};

/** 叠在订单等业务 modal 之上时，抬高 z-index 并模糊下层（见 index.css） */
const NESTED_MODAL_DEEP_CLASS = 'modal-nested-stack-deep';
const NESTED_MODAL_BACKDROP_DEEP = 'modal-backdrop-nested-deep';

const getMatchVariant = (matchReason) => {
	if (matchReason === 'no_match') return 'danger';
	if (matchReason === 'default' || matchReason === 'no_supervisor') return 'warning';
	return 'success';
};

const formatScopeLabel = (rule, role) => {
	if (role === 'assistant' && (rule.matchReason === 'order_level' || (rule.brand === '全部' && rule.category === '全部'))) {
		return '整单';
	}
	if (role === 'salesSupervisor' && rule.matchReason === 'no_supervisor') {
		return '无销售主管';
	}
	const brand = rule.brand || '全部';
	const category = rule.category || '全部';
	return `${brand} / ${category}`;
};

const buildDivergedRuleLabel = (rule) => {
	const roleLabel = ROLE_LABELS[rule?.role] ?? rule?.role ?? '未知角色';
	const ruleTypeLabel = RULE_TYPE_LABELS[rule?.ruleType] ?? rule?.ruleType ?? '规则';
	const scopeLabel = formatScopeLabel(rule, rule?.role);
	return `${roleLabel} · ${ruleTypeLabel} · ${scopeLabel}`;
};

/** 无销售主管时，后端会写入 no_supervisor 占位规则；展示层直接隐藏 */
const filterNoSupervisorPlaceholder = (rules) => rules.filter((r) => !(r.role === 'salesSupervisor' && r.matchReason === 'no_supervisor'));

const formatRuleValue = (rule) => {
	if (rule.ruleType === 'fixed') return formatCurrency(rule.performanceValue ?? 0);
	if (rule.ruleType === 'percentage') return `${Number(rule.performanceValue ?? 0).toFixed(2)}%`;
	if (rule.ruleType === 'profitMarginRanges') {
		const n = Array.isArray(rule.profitMarginRanges) ? rule.profitMarginRanges.length : 0;
		return n > 0 ? `系数 ${Number(rule.performanceValue ?? 1).toFixed(2)} · ${n} 段` : '-';
	}
	return '-';
};

const RuleRow = ({ rule, roleKey, onEdit, onDelete, isDeleting, readOnly, showDivergedBadge, showCoreSaleBadge }) => {
	const ruleTypeLabel = RULE_TYPES.find((r) => r.value === rule.ruleType)?.label ?? rule.ruleType;
	const matchReasonLabel = MATCH_REASON_LABELS[rule.matchReason] ?? rule.matchReason;
	const scopeLabel = formatScopeLabel(rule, roleKey);
	const shouldHideMatchReasonBadge = roleKey === 'assistant' && rule.ruleType === 'fixed' && rule.matchReason === 'order_level';

	return (
		<div className='d-flex align-items-center justify-content-between gap-2 fs-10 fw-normal py-2 border-bottom border-200'>
			<div className='min-w-0'>
				<div className='d-flex align-items-center gap-2 flex-wrap mb-1'>
					<span className='fw-semibold text-900 text-nowrap'>
						<FontAwesomeIcon icon={ROLE_ICONS[roleKey]} className='fa-fw me-1 text-primary' />
						{ROLE_LABELS[roleKey]}
					</span>
					{showCoreSaleBadge ? (
						<SubtleBadge bg='info' className='fs-11' title='核心销售，未配置销售主管'>
							核心销售
						</SubtleBadge>
					) : null}
					<SubtleBadge bg={getRuleTypeBadgeVariant(rule.ruleType)} className='fs-11'>
						{ruleTypeLabel}
					</SubtleBadge>
					{!shouldHideMatchReasonBadge ? (
						<SubtleBadge bg={getMatchVariant(rule.matchReason)} className='fs-11'>
							{matchReasonLabel}
						</SubtleBadge>
					) : null}
					{showDivergedBadge && rule.divergedFromGlobalRule ? (
						<SubtleBadge bg='warning' className='fs-11' title='本规则已与全局绩效规则不一致'>
							不一致
						</SubtleBadge>
					) : null}
				</div>
				<div className='text-muted text-truncate' title={scopeLabel}>
					{scopeLabel}
				</div>
			</div>
			<div className='d-flex align-items-center gap-2 flex-shrink-0'>
				<span className='fw-bold text-900 text-nowrap'>{formatRuleValue(rule)}</span>
				{!readOnly && !rule.isDisplayAggregate && (
					<div className='d-flex gap-1'>
						<Button variant='link' size='sm' className='p-0 text-primary' onClick={() => onEdit(rule)} title='编辑'>
							<FontAwesomeIcon icon={faPen} />
						</Button>
						<Button variant='link' size='sm' className='p-0 text-danger' onClick={() => onDelete(rule)} disabled={isDeleting} title='删除'>
							<FontAwesomeIcon icon={faTrash} />
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

const ROLE_SORT_INDEX = Object.fromEntries(ROLE_ORDER.map((key, index) => [key, index]));

const RulesFlatList = ({ rules, onEdit, onDelete, isDeleting, readOnly, showDivergedBadge, showCoreSaleNoSupervisorBadge }) => {
	const sortedRules = useMemo(() => [...rules].sort((a, b) => (ROLE_SORT_INDEX[a.role] ?? 9) - (ROLE_SORT_INDEX[b.role] ?? 9)), [rules]);

	if (sortedRules.length === 0) return null;

	return (
		<div className='performance-rules-flat-list'>
			{sortedRules.map((rule) => (
				<RuleRow
					key={rule._id}
					rule={rule}
					roleKey={rule.role}
					onEdit={onEdit}
					onDelete={onDelete}
					isDeleting={isDeleting}
					readOnly={readOnly}
					showDivergedBadge={showDivergedBadge}
					showCoreSaleBadge={rule.role === 'sale' && showCoreSaleNoSupervisorBadge}
				/>
			))}
		</div>
	);
};

const EditRuleModal = ({ show, onHide, rule, onSave, isSaving }) => {
	const [performanceValue, setPerformanceValue] = useState(0);
	const [profitMarginRanges, setProfitMarginRanges] = useState([]);

	useEffect(() => {
		if (rule) {
			setPerformanceValue(rule.performanceValue ?? 0);
			setProfitMarginRanges(Array.isArray(rule.profitMarginRanges) ? [...rule.profitMarginRanges] : []);
		}
	}, [rule]);

	const handleSave = useCallback(async () => {
		if (isSaving) return;
		if (rule?.ruleType === 'fixed' || rule?.ruleType === 'percentage') {
			await onSave({ performanceValue: Number(performanceValue) });
			return;
		}
		if (rule?.ruleType === 'profitMarginRanges' && Array.isArray(profitMarginRanges)) {
			const coeff = Number(performanceValue);
			if (isNaN(coeff) || coeff < 0) {
				toast.error('校正系数必须大于等于0');
				return;
			}
			await onSave({ performanceValue: coeff, profitMarginRanges });
		}
	}, [rule?.ruleType, performanceValue, profitMarginRanges, onSave, isSaving]);

	return (
		<Modal
			show={show}
			onHide={() => !isSaving && onHide()}
			centered
			className={NESTED_MODAL_DEEP_CLASS}
			backdropClassName={NESTED_MODAL_BACKDROP_DEEP}
			size={rule?.ruleType === 'profitMarginRanges' ? 'lg' : undefined}
			dialogClassName={rule?.ruleType === 'profitMarginRanges' ? 'modal-dialog-profit-margin' : undefined}
		>
			<Modal.Header closeButton={!isSaving} className='border-bottom'>
				<Modal.Title className='fs-6 d-flex align-items-center gap-2'>
					编辑绩效规则
					{isSaving ? (
						<span className='fs-10 fw-normal text-primary d-inline-flex align-items-center gap-1'>
							<FontAwesomeIcon icon={faSync} spin />
							保存中…
						</span>
					) : null}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className='position-relative' style={{ minHeight: isSaving ? '8rem' : undefined }}>
				<PerformanceRulesRematchOverlay show={isSaving} label='正在保存绩效规则…' />
				<AppScrollArea maxHeight='70vh' style={{ pointerEvents: isSaving ? 'none' : undefined, opacity: isSaving ? 0.65 : 1 }}>
					{rule?.ruleType === 'fixed' && (
						<div className='mb-2'>
							<label htmlFor='orderPerformanceRuleFixedValue' className='form-label small fw-semibold'>
								固定金额
							</label>
							<input
								id='orderPerformanceRuleFixedValue'
								name='orderPerformanceRuleFixedValue'
								type='number'
								className='form-control form-control-sm'
								value={performanceValue}
								onChange={(e) => setPerformanceValue(e.target.value)}
								step='0.01'
							/>
						</div>
					)}
					{rule?.ruleType === 'percentage' && (
						<div className='mb-2'>
							<label htmlFor='orderPerformanceRulePercentageValue' className='form-label small fw-semibold'>
								百分比 (%)
							</label>
							<input
								id='orderPerformanceRulePercentageValue'
								name='orderPerformanceRulePercentageValue'
								type='number'
								className='form-control form-control-sm'
								value={performanceValue}
								onChange={(e) => setPerformanceValue(e.target.value)}
								step='0.01'
							/>
						</div>
					)}
					{rule?.ruleType === 'profitMarginRanges' && (
						<>
							<Row>
								<Col sm={4}>
									<label htmlFor='orderPerformanceRuleCorrectionFactor' className='form-label small fw-semibold'>
										校正系数
									</label>
									<input
										id='orderPerformanceRuleCorrectionFactor'
										name='orderPerformanceRuleCorrectionFactor'
										type='number'
										className='form-control form-control-sm'
										value={performanceValue}
										onChange={(e) => setPerformanceValue(e.target.value)}
										step='0.01'
										min='0'
										placeholder='例如：1.0'
									/>
									<small className='text-muted'>用于调整绩效计算结果</small>
								</Col>
							</Row>
							<div className='mt-3'>
								<label className='form-label small fw-semibold'>利润率分段</label>
								<ProfitMarginRangesEditor ranges={profitMarginRanges} onRangesChange={setProfitMarginRanges} mode='edit' />
							</div>
						</>
					)}
				</AppScrollArea>
			</Modal.Body>
			<Modal.Footer className='border-top py-2'>
				<Button variant='outline-secondary' size='sm' onClick={() => !isSaving && onHide()} disabled={isSaving}>
					取消
				</Button>
				<Button variant='primary' size='sm' onClick={handleSave} disabled={isSaving}>
					{isSaving ? (
						<>
							<FontAwesomeIcon icon={faSync} spin className='me-1' />
							保存中...
						</>
					) : (
						'保存'
					)}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

const OrderPerformanceRuleDetailSection = ({ embedded = false }) => {
	const { orderDetailUi } = useOrderPerformanceFeatures();
	const { order, isOrderReady } = useOrderContext();
	const viewOnly = useOrderDetailViewOnly();
	const canViewSettlement = Boolean(order?.viewerCapabilities?.canViewPerformanceSettlement);
	const actionDisabled = !isOrderReady;
	const { rematchRenderHold, setRematchRenderHold } = useOrderPerformanceRulesRematchHold();
	const [reMatch, { isLoading: isRematching }] = useReMatchOrderPerformanceRulesMutation();
	const isRematchingUi = isRematching || rematchRenderHold;
	const [updateRule, { isLoading: isUpdating }] = useUpdateOrderPerformanceRuleMutation();
	const [deleteRule, { isLoading: isDeleting }] = useDeleteOrderPerformanceRuleMutation();
	const [editRule, setEditRule] = useState(null);
	const [confirmDeleteRule, setConfirmDeleteRule] = useState(null);

	const orderOId = order?._id;
	const canEditBasis =
		order?.uiHints?.canEditPerformanceRules ?? (!order?.isCompleted && !order?.isFinalCostConfirmed);
	const performanceReadOnly = viewOnly || !canEditBasis;

	const roleGroups = useMemo(() => {
		const snap = order?.performanceRulesSnapshot ?? {};
		return ROLE_ORDER.map((key) => {
			const source = key === 'assistant' ? (snap.assistantRules ?? []) : key === 'sale' ? (snap.saleRules ?? []) : (snap.salesSupervisorRules ?? []);
			const filteredSource = key === 'salesSupervisor' ? filterNoSupervisorPlaceholder(source) : source;
			return {
				key,
				rules: filteredSource.map((r) => ({ ...r, role: key })),
			};
		});
	}, [order?.performanceRulesSnapshot]);

	const allRules = useMemo(() => roleGroups.flatMap((g) => g.rules), [roleGroups]);
	const displayRules = allRules;
	const showCoreSaleNoSupervisorBadge = Boolean(order?.uiHints?.isCoreSaleWithoutSupervisor);
	const hasBasis = getOrderPerformanceBasisView(order).hasBasis;
	const performanceRulesDiverged = Boolean(order?.performanceRulesDivergedFromGlobal);
	const divergedRuleDetails = useMemo(
		() =>
			Array.from(
				new Set(
					displayRules
						.filter((rule) => rule?.divergedFromGlobalRule)
						.map((rule) => buildDivergedRuleLabel(rule)),
				),
			),
		[displayRules],
	);

	const runRematch = useCallback(async () => {
		if (!orderOId || isRematchingUi) return;
		setRematchRenderHold(true);
		try {
			markLocalOrderMutation(orderOId);
			await reMatch({ orderId: orderOId }).unwrap();
			markLocalOrderMutation(orderOId);
			await waitForNextPaint();
			toast.success('绩效规则已重新匹配');
		} catch (err) {
			toast.error(err?.data?.message || err?.error || '重新匹配失败');
		} finally {
			setRematchRenderHold(false);
		}
	}, [orderOId, reMatch, isRematchingUi, setRematchRenderHold]);

	const handleReMatchClick = useCallback(() => {
		if (!orderOId || performanceReadOnly) return;
		runRematch();
	}, [orderOId, performanceReadOnly, runRematch]);

	const performanceRulesBusy = isPerformanceRulesBusy({ isRematching: isRematchingUi, isUpdating, isDeleting });

	const handleEditSave = useCallback(
		async (payload) => {
			if (!orderOId || !editRule?._id || performanceReadOnly || isUpdating) return;
			try {
				markLocalOrderMutation(orderOId);
				await updateRule({ orderId: orderOId, ruleId: editRule._id, ...payload }).unwrap();
				markLocalOrderMutation(orderOId);
				await waitForNextPaint();
				toast.success('规则已更新');
				setEditRule(null);
			} catch (err) {
				toast.error(err?.data?.message || err?.error || '更新失败');
			}
		},
		[orderOId, editRule, performanceReadOnly, isUpdating, updateRule],
	);

	const handleDelete = useCallback(
		(rule) => {
			if (!orderOId || !rule?._id || performanceReadOnly) return;
			setConfirmDeleteRule(rule);
		},
		[orderOId, performanceReadOnly],
	);

	const handleConfirmDelete = useCallback(async () => {
		if (!orderOId || !confirmDeleteRule?._id || performanceReadOnly) return;
		try {
			markLocalOrderMutation(orderOId);
			await deleteRule({ orderId: orderOId, ruleId: confirmDeleteRule._id }).unwrap();
			markLocalOrderMutation(orderOId);
			await waitForNextPaint();
			toast.success('规则已删除');
			setConfirmDeleteRule(null);
		} catch (err) {
			toast.error(err?.data?.message || err?.error || '删除失败');
		}
	}, [orderOId, confirmDeleteRule, performanceReadOnly, deleteRule]);

	if (!canViewSettlement || !orderDetailUi || !orderOId) return null;

	const phaseHint = !hasBasis
		? '请先重新匹配绩效规则'
		: !canEditBasis
			? order?.isFinalCostConfirmed
				? '已确认最终成本，绩效规则不可再编辑'
				: '已完成，绩效规则不可再编辑'
			: null;

	const rematchButton = canEditBasis && !viewOnly ? (
		<Button variant='outline-primary' size='sm' className='fs-10 fw-semibold' onClick={handleReMatchClick} disabled={actionDisabled || performanceRulesBusy}>
			重匹配
		</Button>
	) : null;

	return (
		<div className={`d-flex flex-column gap-2${embedded ? '' : ' pt-1'}`}>
			<SystemAlert
				title='提示内容'
				bodyText={`你确定要删除该绩效规则吗？${confirmDeleteRule ? `（${formatScopeLabel(confirmDeleteRule, confirmDeleteRule.role)}）` : ''}`}
				cancelText='手滑了'
				confirmText='确定删除'
				confirmColor='danger'
				position='top'
				confirmWaiting={isDeleting}
				modalShow={Boolean(confirmDeleteRule)}
				setModalShow={() => !isDeleting && setConfirmDeleteRule(null)}
				handleConfirm={handleConfirmDelete}
			/>
			{performanceRulesDiverged ? (
				<Alert variant='warning' className='py-2 px-3 mb-0 fs-10'>
					<div className='fw-semibold'>检测到 {divergedRuleDetails.length || '多条'} 绩效规则与全局规则不一致</div>
					{divergedRuleDetails.length > 0 ? (
						<ul className='mb-0 mt-1 ps-3'>
							{divergedRuleDetails.slice(0, 3).map((detail) => (
								<li key={detail}>{detail}</li>
							))}
							{divergedRuleDetails.length > 3 ? <li>等 {divergedRuleDetails.length} 条</li> : null}
						</ul>
					) : (
						<div className='mt-1'>请核对后再确认最终成本或重匹配。</div>
					)}
				</Alert>
			) : null}
			{phaseHint ? <p className='fs-10 text-muted mb-0'>{phaseHint}</p> : null}
			{embedded ? (
				<>
					<div className='d-flex align-items-center justify-content-between gap-2'>
						<span className='fs-10 text-600'>{allRules.length === 0 ? '暂无绩效规则' : `共 ${displayRules.length} 条绩效规则`}</span>
						{rematchButton}
					</div>
					{allRules.length > 0 ? (
						<div style={{ pointerEvents: performanceRulesBusy ? 'none' : undefined, opacity: performanceRulesBusy ? 0.65 : 1 }}>
							<RulesFlatList
								rules={displayRules}
								onEdit={setEditRule}
								onDelete={handleDelete}
								isDeleting={isDeleting}
								readOnly={performanceReadOnly || performanceRulesBusy}
								showDivergedBadge
								showCoreSaleNoSupervisorBadge={showCoreSaleNoSupervisorBadge}
							/>
						</div>
					) : null}
				</>
			) : allRules.length === 0 ? (
				<div className='border rounded-2 overflow-hidden'>
					<div className='d-flex align-items-center justify-content-between gap-2 px-3 py-2 bg-body-tertiary'>
						<span className='fs-10 fw-semibold text-700'>绩效规则</span>
						{rematchButton}
					</div>
					<div className='text-muted fs-10 py-3 px-2 text-center'>暂无绩效规则</div>
				</div>
			) : (
				<div className='border rounded-2 overflow-hidden'>
					<div className='d-flex align-items-center justify-content-between gap-2 px-3 py-2 bg-body-tertiary border-bottom'>
						<div className='d-flex align-items-center gap-2 min-w-0'>
							<span className='fs-10 fw-semibold text-700'>绩效规则</span>
							<Badge bg='secondary' className='fs-11'>
								{displayRules.length}
							</Badge>
						</div>
						{rematchButton}
					</div>
					<div className='px-3 position-relative' style={{ pointerEvents: performanceRulesBusy ? 'none' : undefined, opacity: performanceRulesBusy ? 0.65 : 1 }}>
						<RulesFlatList
							rules={displayRules}
							onEdit={setEditRule}
							onDelete={handleDelete}
							isDeleting={isDeleting}
							readOnly={performanceReadOnly || performanceRulesBusy}
							showDivergedBadge
							showCoreSaleNoSupervisorBadge={showCoreSaleNoSupervisorBadge}
						/>
					</div>
				</div>
			)}
			<EditRuleModal
				key={editRule?._id ?? 'modal'}
				show={!!editRule}
				onHide={() => !isUpdating && setEditRule(null)}
				rule={editRule}
				onSave={handleEditSave}
				isSaving={isUpdating}
			/>
		</div>
	);
};

OrderPerformanceRuleDetailSection.propTypes = {
	embedded: PropTypes.bool,
};

export default OrderPerformanceRuleDetailSection;
